import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Calendar, Users, MapPin, Lock, Wallet, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useOfflineCache } from "../hooks/useOfflineCache";

// Sub-components
import { getTripState, PendingValidation, ExpiredAccess } from "../components/trip/TripStates";
import { PaymentWall } from "../components/trip/PaymentWall";
import { TripChatbot } from "../components/trip/TripChatbot";
import { DocumentsTab } from "../components/trip/DocumentsTab";
import { ItineraryTab } from "../components/trip/tabs/ItineraryTab";
import { BudgetTab } from "../components/trip/tabs/BudgetTab";
import { FlightsTab } from "../components/trip/tabs/FlightsTab";
import { PracticalInfoTab } from "../components/trip/tabs/PracticalInfoTab";
import { PreDepartureTab } from "../components/trip/tabs/PreDepartureTab";
import { PDFExportButton } from "../components/trip/PDFExportButton";
import { RoadmapTab } from "../components/trip/roadmap/RoadmapTab";
import { TripMapTab } from "../components/trip/map/TripMapTab";
import { PLAN_CONFIG } from "../config/tripPlans";

const ALL_TABS = [
  { value: "itineraire",   label: "Itinéraire" },
  { value: "roadmap",      label: "🗺️ Feuille de Route" },
  { value: "carte",        label: "📍 Carte" },
  { value: "budget",       label: "Budget" },
  { value: "infos",        label: "Infos pratiques" },
  { value: "vols",         label: "Vols & réservations" },
  { value: "documents",    label: "Documents" },
];

export default function TripPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Offline cache hook — used to auto-cache on payment success
  const { cacheTrip } = useOfflineCache(id);

  const loadTrip = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${id}`);
      // Hors-ligne sans cache → le SW renvoie {offline:true}
      if (data && data.offline) {
        setError("Ce voyage n'est pas encore disponible hors connexion. Ouvrez-le une fois en ligne pour le consulter ensuite sans réseau.");
      } else {
        setTrip(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Voyage introuvable");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTrip(); }, [loadTrip]);

  // Met automatiquement en cache le dernier itinéraire consulté (+ Feuille de
  // Route) pour un accès hors connexion — sans action de l'utilisateur.
  const autoCachedRef = useRef(null);
  useEffect(() => {
    if (
      trip && trip.payment_status === "paid" && trip.admin_validated &&
      navigator.onLine && autoCachedRef.current !== id
    ) {
      autoCachedRef.current = id;
      cacheTrip();
      try { localStorage.setItem("voyage_r_last_trip", id); } catch { /* quota */ }
    }
  }, [trip, id, cacheTrip]);

  // Save active trip destination to localStorage for BudgetPage auto-detection
  useEffect(() => {
    if (trip?.destination) {
      try {
        localStorage.setItem("voyage_r_active_trip", JSON.stringify({ destination: trip.destination, tripId: id }));
      } catch (e) {
        console.warn("[TripPage] Could not save to localStorage:", e);
      }
    }
  }, [trip, id]);

  // Dérivé de location.search — valeur primitive stable, change uniquement si l'URL change
  const sessionId = new URLSearchParams(location.search).get("session_id");

  // Poll payment status after returning from Stripe
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    setCheckingPayment(true);
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          toast.success("Paiement confirmé ! Votre voyage est débloqué.");
          setCheckingPayment(false);
          navigate(`/trip/${id}`, { replace: true });
          await loadTrip();
          // Auto-cache the trip for offline access after purchase
          cacheTrip().then((ok) => {
            if (ok) toast("Itinéraire sauvegardé — consultable hors-ligne", {
              duration: 4000,
              style: { fontFamily: "'Lora', Georgia, serif" },
            });
          });
          return;
        }
        if (data.status === "expired") {
          setCheckingPayment(false);
          toast.error("La session de paiement a expiré.");
          navigate(`/trip/${id}`, { replace: true });
          return;
        }
        if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
          navigate(`/trip/${id}`, { replace: true });
        }
      } catch {
        if (cancelled) return;
        setCheckingPayment(false);
        navigate(`/trip/${id}`, { replace: true });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, id, navigate, loadTrip, cacheTrip]);

  if (loading || checkingPayment) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F9F6F0" }}>
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {checkingPayment ? "Vérification du paiement..." : "Chargement..."}
        </p>
      </div>
    </div>
  );

  if (error || !trip) return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <h2 className="text-2xl mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>Voyage introuvable</h2>
        <p className="text-[#5C554E] mb-6 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>{error}</p>
        <button onClick={() => navigate("/dashboard")} className="btn-primary">Retour au tableau de bord</button>
      </div>
    </div>
  );

  const tripState = getTripState(trip, isAdmin);
  const result = trip.result || {};
  const currencyCode = result?.infos_pratiques?.monnaie_code || "USD";
  const planCfg = PLAN_CONFIG[trip.plan_type] || PLAN_CONFIG.serenite;
  const allowedTabs = planCfg.tabs;
  const visibleTabs = ALL_TABS.filter((t) => allowedTabs.includes(t.value));

  // "Avant le départ" tab: only for a_venir trips (paid + validated + not started)
  const isUpcoming = trip.trip_status === "a_venir" || (
    trip.payment_status === "paid" && trip.admin_validated &&
    trip.date_depart && new Date(trip.date_depart) > new Date()
  );
  const AVANT_DEPART_TAB = { value: "avant-depart", label: "Avant le départ" };
  const finalTabs = isUpcoming
    ? [AVANT_DEPART_TAB, ...visibleTabs]
    : visibleTabs;
  const defaultTab = isUpcoming ? "avant-depart" : "itineraire";

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />

      {/* Hero header */}
      <div className="bg-[#2B2623] text-[#F9F6F0]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#DCD4C6] hover:text-[#F9F6F0] text-sm mb-5 transition-colors"
            style={{ fontFamily: "'Lora', Georgia, serif" }}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={15} strokeWidth={1.5} /> Mes voyages
          </button>
          <h1
            className="text-4xl sm:text-5xl mb-3"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
            data-testid="trip-destination"
          >
            {trip.destination}
          </h1>
          <div className="flex flex-wrap gap-5 text-sm text-[#DCD4C6]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            <span className="flex items-center gap-1.5"><Calendar size={13} strokeWidth={1.5} /> {trip.date_depart} — {trip.date_retour}</span>
            <span className="flex items-center gap-1.5"><Users size={13} strokeWidth={1.5} /> {trip.nb_adultes} adulte{trip.nb_adultes > 1 ? "s" : ""}{trip.nb_enfants > 0 ? `, ${trip.nb_enfants} enfant${trip.nb_enfants > 1 ? "s" : ""}` : ""}</span>
            <span className="flex items-center gap-1.5"><MapPin size={13} strokeWidth={1.5} /> Depuis {trip.ville_depart}</span>
          </div>

          {/* Budget summary — shown only when budget was entered precisely */}
          {trip.budget_montant && (
            <div className="mt-3 pt-3 border-t border-[#F9F6F0]/10 flex flex-wrap items-center gap-x-4 gap-y-1.5" style={{ fontFamily: "'Lora', Georgia, serif" }} data-testid="trip-budget-summary">
              <span className="flex items-center gap-1.5 text-sm">
                <Wallet size={13} strokeWidth={1.5} className="text-[#C17F59] flex-shrink-0" />
                <strong className="text-[#F9F6F0]">{trip.budget_montant.toLocaleString("fr-FR")} €</strong>
                <span className="text-[#9A8F86]">{trip.budget_type === "par_personne" ? "par personne" : "total"}</span>
                {trip.vols_inclus && <span className="text-[#9A8F86] text-xs">· vols réservés</span>}
              </span>
              {[
                trip.repartition_hebergement ? `Hébergement ${trip.repartition_hebergement}%` : null,
                trip.repartition_restauration ? `Restauration ${trip.repartition_restauration}%` : null,
                trip.repartition_activites   ? `Activités ${trip.repartition_activites}%`        : null,
                trip.repartition_transport   ? `Transport ${trip.repartition_transport}%`         : null,
              ].filter(Boolean).map((item) => (
                <span key={item} className="text-xs text-[#9A8F86] border border-[#F9F6F0]/10 px-2 py-0.5 rounded-sm">{item}</span>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            {trip.admin_validated && (
              <span className="bg-[#C17F59]/20 text-[#C17F59] border border-[#C17F59]/40 text-xs px-3 py-1 rounded-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                Validé par Diego
              </span>
            )}
            {trip.payment_status === "paid" && (
              <span className="bg-emerald-900/30 text-emerald-300 border border-emerald-700/40 text-xs px-3 py-1 rounded-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                Débloqué
              </span>
            )}
            {!trip.admin_validated && (
              <span className="bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs px-3 py-1 rounded-sm" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                En cours de validation
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content based on trip state */}
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        {tripState === "expired" && <ExpiredAccess trip={trip} />}
        {tripState === "pending" && <PendingValidation trip={trip} />}
        {tripState === "payment" && (
          <>
            <PaymentWall trip={trip} onPaymentSuccess={loadTrip} />

            {/* ── Aperçu flouté de l'itinéraire ─────────────────────────────── */}
            <div className="mt-2 mb-8">
              <div className="text-center mb-4">
                <p className="text-xs uppercase tracking-widest text-[#9A8F86]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  Aperçu de votre itinéraire
                </p>
              </div>
              <div className="relative rounded-sm overflow-hidden" style={{ maxHeight: "420px" }}>
                {/* Contenu flouté */}
                <div style={{ filter: "blur(5px)", pointerEvents: "none", userSelect: "none", opacity: 0.85 }}>
                  <ItineraryTab result={result} trip={trip} />
                </div>

                {/* Dégradé crème du bas */}
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to bottom, rgba(249,246,240,0) 0%, rgba(249,246,240,0.5) 40%, rgba(249,246,240,0.95) 75%, #F9F6F0 100%)"
                }} />

                {/* Overlay central — icône + texte */}
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div className="bg-[#F9F6F0]/90 backdrop-blur-sm border border-[#DCD4C6] px-8 py-5 text-center rounded-sm shadow-sm" style={{ maxWidth: "320px" }}>
                    <div className="w-12 h-12 border border-[#DCD4C6] rounded-full flex items-center justify-center mx-auto mb-3">
                      <Lock size={20} strokeWidth={1.5} className="text-[#C17F59]" />
                    </div>
                    <p className="text-sm text-[#2B2623]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: "1.1rem" }}>
                      Votre voyage vous attend
                    </p>
                    <p className="text-xs text-[#9A8F86] italic mt-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                      Débloquez l&apos;offre de votre choix pour accéder à l&apos;itinéraire complet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {tripState === "full" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <Tabs defaultValue={defaultTab} className="w-full" data-testid="trip-tabs">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <TabsList className="bg-[#F1EBE1] border border-[#DCD4C6] p-1 flex flex-wrap gap-1 h-auto rounded-sm flex-1">
                  {finalTabs.map(({ value, label }) => (
                    <TabsTrigger
                      key={value}
                      value={value}
                      className="text-sm rounded-sm data-[state=active]:bg-[#C17F59] data-[state=active]:text-[#F9F6F0]"
                      style={{ fontFamily: "'Lora', Georgia, serif" }}
                      data-testid={`tab-${value}`}
                    >
                      {value === "avant-depart" && (
                        <ClipboardCheck size={13} strokeWidth={1.5} className="mr-1.5 inline" />
                      )}
                      {label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <PDFExportButton trip={trip} />
              </div>
              <TabsContent value="avant-depart">
                <PreDepartureTab tripId={id} dateDepart={trip.date_depart} />
              </TabsContent>
              <TabsContent value="itineraire"><ItineraryTab result={result} trip={trip} /></TabsContent>
              <TabsContent value="roadmap"><RoadmapTab tripId={id} trip={trip} /></TabsContent>
              <TabsContent value="carte"><TripMapTab tripId={id} trip={trip} /></TabsContent>
              <TabsContent value="budget"><BudgetTab result={result} currencyCode={currencyCode} /></TabsContent>
              <TabsContent value="infos"><PracticalInfoTab result={result} destination={trip.destination} tripId={id} planType={trip.plan_type} isAdmin={isAdmin} /></TabsContent>
              <TabsContent value="vols"><FlightsTab trip={trip} /></TabsContent>
              <TabsContent value="documents"><DocumentsTab tripId={id} /></TabsContent>
            </Tabs>
            </div>
            {planCfg.chatbot && <TripChatbot tripId={id} />}
          </>
        )}
      </div>
    </div>
  );
}
